/**
 * Schémas Zod du module Dégorgement (Lot D) — partagés API ⇄ ERP.
 *
 * Le DÉGORGEMENT expulse le dépôt et pose le dosage final : opération IMMUABLE
 * (trigger 0059) qui scinde le lot source (lattes/pointes) et crée le lot
 * dégorgé avec SON dosage. Un tirage se dégorge en N fois, dosages différents.
 * Source des énumérations/CHECKs : migration `0058_lot_d_cave.sql` (`degorgements`).
 * Bornes des mentions : règl. (UE) 2019/33 annexe III partie A — chevauchements
 * VOULUS (l'app PROPOSE les mentions compatibles, l'utilisateur CHOISIT).
 */

import { z } from "zod";

import {
  compositionResultatDegorgementSchema,
  sourcesAppointDegorgementSchema,
  sourceAppointDetailSchema,
} from "./degorgement-appoint.js";
import { matierePoseeSchema } from "./matiere-posee.js";
import { idSchema } from "./parcellaire.js";
import { decisionQualificationDegorgementSchema } from "./qualification-degorgement.js";
import { dosageGLSchema, nbBouteillesSchema, NB_BOUTEILLES_MAX } from "./tirage.js";
import { dateIsoSchema } from "./vendange.js";

// ────────────────────────────────────────────────────────────────────────────
// Énumérations (miroir des CHECK SQL)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Catégorie de dosage (mention étiquette) — miroir CHECK SQL `categorie_dosage`
 * (7 mentions, règl. (UE) 2019/33 annexe III). Partagée avec cuvée commerciale
 * (cible) et reprise coopérative.
 */
export const categorieDosageSchema = z.enum([
  "brut_nature",
  "extra_brut",
  "brut",
  "extra_dry",
  "sec",
  "demi_sec",
  "doux",
]);
export type CategorieDosage = z.infer<typeof categorieDosageSchema>;

/** Méthode de dégorgement — miroir CHECK SQL `degorgements.methode`. */
export const methodeDegorgementSchema = z.enum(["glace", "volee"]);
export type MethodeDegorgement = z.infer<typeof methodeDegorgementSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Bornes UE des mentions de dosage (règl. 2019/33 annexe III partie A)
// ────────────────────────────────────────────────────────────────────────────

/** Tolérance étiquette : teneur réelle vs mention, écart ≤ 3 g/L (règl. 2019/33). */
export const TOLERANCE_ETIQUETTE_DOSAGE_G_L = 3;

/** Bornes d'une mention : intervalle [min, max] éventuellement ouvert/semi-ouvert. */
export interface CategorieDosageBorne {
  code: CategorieDosage;
  libelle: string;
  min_g_l: number;
  min_inclus: boolean;
  /** null = pas de borne haute (doux). */
  max_g_l: number | null;
  max_inclus: boolean;
  /** brut_nature : AUCUNE adjonction de sucre après prise de mousse. */
  exige_non_dose: boolean;
}

/**
 * Les 7 mentions, bornes UE EXACTES. Chevauchements réels et voulus (bornes
 * partagées 12/17/32/50, plages englobantes 0-6 ⊂ 0-12) : `mentionsCompatibles`
 * peut renvoyer PLUSIEURS mentions — l'UI fait choisir, jamais de pré-cochage.
 */
export const CATEGORIES_DOSAGE: readonly CategorieDosageBorne[] = [
  // < 3 g/L ET aucune adjonction post prise de mousse.
  {
    code: "brut_nature",
    libelle: "Brut nature",
    min_g_l: 0,
    min_inclus: true,
    max_g_l: 3,
    max_inclus: false,
    exige_non_dose: true,
  },
  // Entre 0 et 6 g/L.
  {
    code: "extra_brut",
    libelle: "Extra brut",
    min_g_l: 0,
    min_inclus: true,
    max_g_l: 6,
    max_inclus: true,
    exige_non_dose: false,
  },
  // < 12 g/L (strict).
  {
    code: "brut",
    libelle: "Brut",
    min_g_l: 0,
    min_inclus: true,
    max_g_l: 12,
    max_inclus: false,
    exige_non_dose: false,
  },
  // Entre 12 et 17 g/L.
  {
    code: "extra_dry",
    libelle: "Extra dry",
    min_g_l: 12,
    min_inclus: true,
    max_g_l: 17,
    max_inclus: true,
    exige_non_dose: false,
  },
  // Entre 17 et 32 g/L.
  {
    code: "sec",
    libelle: "Sec",
    min_g_l: 17,
    min_inclus: true,
    max_g_l: 32,
    max_inclus: true,
    exige_non_dose: false,
  },
  // Entre 32 et 50 g/L.
  {
    code: "demi_sec",
    libelle: "Demi-sec",
    min_g_l: 32,
    min_inclus: true,
    max_g_l: 50,
    max_inclus: true,
    exige_non_dose: false,
  },
  // > 50 g/L (strict).
  {
    code: "doux",
    libelle: "Doux",
    min_g_l: 50,
    min_inclus: false,
    max_g_l: null,
    max_inclus: false,
    exige_non_dose: false,
  },
];

/**
 * Mentions compatibles avec un dosage final réel (g/L). `estDose` = une liqueur
 * d'expédition a été ajoutée (brut_nature exige NON dosé). Fonction PURE,
 * partagée backend (contrôle bloquant) ⇄ frontend (boutons de mention É6).
 * Dosage négatif ou non fini → aucune mention (garde-fou).
 */
export function mentionsCompatibles(dosageGL: number, estDose: boolean): CategorieDosage[] {
  if (!Number.isFinite(dosageGL) || dosageGL < 0) return [];
  return CATEGORIES_DOSAGE.filter((categorie) => {
    if (categorie.exige_non_dose && estDose) return false;
    const minOk = categorie.min_inclus
      ? dosageGL >= categorie.min_g_l
      : dosageGL > categorie.min_g_l;
    const maxOk =
      categorie.max_g_l === null ||
      (categorie.max_inclus ? dosageGL <= categorie.max_g_l : dosageGL < categorie.max_g_l);
    return minOk && maxOk;
  }).map((categorie) => categorie.code);
}

// ────────────────────────────────────────────────────────────────────────────
// Bilan physique de la liqueur d'expédition (CdC Champagne IX-2°-f)
// ────────────────────────────────────────────────────────────────────────────

/** Tolérance de calcul, en point de pourcentage, pour les arrondis de saisie. */
export const TOLERANCE_BILAN_DOSAGE_PCT = 0.01;

export interface BilanDosageEntree {
  nbBouteillesFinales: number;
  formatCl: number;
  volumeLiqueurL: number;
  teneurSucreAvantGL: number;
  teneurSucreFinaleGL: number;
  augmentationTavPctVol: number;
}

export interface BilanDosageResultat {
  augmentationSucreGL: number;
  augmentationVolumePct: number;
  plafondAugmentationVolumePct: number;
  conforme: boolean;
}

/**
 * Fige le contrôle `V <= 1,266 × A + 0,0666 × S` du cahier des charges.
 * V est calculé sur le vin avant ajout de liqueur ; les valeurs physiques
 * restent non arrondies jusqu'à la persistance NUMERIC.
 */
export function calculerBilanDosage(entree: BilanDosageEntree): BilanDosageResultat {
  const volumeFinalL = entree.nbBouteillesFinales * (entree.formatCl / 100);
  const volumeAvantLiqueurL = volumeFinalL - entree.volumeLiqueurL;
  const augmentationSucreGL = Math.max(0, entree.teneurSucreFinaleGL - entree.teneurSucreAvantGL);
  const augmentationVolumePct =
    entree.volumeLiqueurL > 0 && volumeAvantLiqueurL > 0
      ? (entree.volumeLiqueurL / volumeAvantLiqueurL) * 100
      : 0;
  const plafondAugmentationVolumePct =
    1.266 * entree.augmentationTavPctVol + 0.0666 * augmentationSucreGL;
  return {
    augmentationSucreGL,
    augmentationVolumePct,
    plafondAugmentationVolumePct,
    conforme:
      volumeAvantLiqueurL > 0 &&
      augmentationVolumePct <= plafondAugmentationVolumePct + TOLERANCE_BILAN_DOSAGE_PCT,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Schémas atomiques
// ────────────────────────────────────────────────────────────────────────────

/** Notes libres (multi-ligne) optionnelles — vide normalisé en undefined. */
const notesSchema = z
  .string()
  .trim()
  .max(4000, "Notes trop longues (4000 caractères max).")
  .optional()
  .or(z.literal("").transform(() => undefined));

/** Matière sèche du domaine prélevée sur un lot fournisseur identifié. */
export const matiereDegorgementSchema = matierePoseeSchema;
export type MatiereDegorgement = z.infer<typeof matiereDegorgementSchema>;

/** Constat documentaire à rapprocher : ne crée aucune disponibilité en stock. */
export const ORIGINES_RELIQUAT_LABELS = {
  bouteilles_appoint: "Bouteilles d’appoint",
  liqueur: "Liqueur",
  melange_doseuse: "Mélange dans la doseuse",
  inconnue: "Origine inconnue",
} as const;
export const reliquatConstateSchema = z.object({
  origine: z.enum(["bouteilles_appoint", "liqueur", "melange_doseuse", "inconnue"]),
  volume_mesure_l: z.number().finite().positive().max(1_000_000).multipleOf(0.001),
  contenant_id: idSchema,
  reference_source: z
    .string()
    .trim()
    .min(1, "Indiquez la référence source ou la provenance inconnue.")
    .max(500),
});
export type ReliquatConstate = z.infer<typeof reliquatConstateSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Input — enregistrement d'un dégorgement (opération immuable)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Input `degorgement.creer` — dégorger tout ou partie d'un lot lattes/pointes.
 *
 * Effets (transaction) : INSERT `degorgements` + nouveau lot `degorgee` +
 * paire de mouvements `degorgement` (+ `casse` si pertes). Invariants use case :
 * date ≥ date_tirage_reference + 12 mois, sinon confirmation du fait réel ; nb + pertes ≤ solde source.
 * `est_dose` est figé avec le bilan de dosage comme témoin de l'ajout réel de
 * liqueur (brut_nature ⇔ aucune liqueur d'expédition).
 */
export const degorgementCreerInput = z
  .object({
    /** Lot source (stade `sur_lattes` ou `sur_pointes`, maison OU repris coop). */
    lot_source_id: idSchema,
    /** Date de la session de dégorgement. */
    date_degorgement: dateIsoSchema,
    /** Confirme un fait déjà réalisé avant les 12 mois, sans le rendre conforme. */
    confirmation_hors_echeance: z.boolean().optional(),
    /** Bouteilles dégorgées (hors pertes). */
    nb_bouteilles: nbBouteillesSchema,
    /** Bouteilles réellement perdues ou cassées — le dépôt expulsé n'est pas une bouteille. */
    nb_pertes: z
      .number({ invalid_type_error: "Pertes invalides." })
      .int("Les pertes doivent être un nombre entier de bouteilles.")
      .min(0, "Les pertes ne peuvent être négatives.")
      .max(NB_BOUTEILLES_MAX, `Pertes maximum : ${NB_BOUTEILLES_MAX} bouteilles.`)
      .default(0),
    /** Bouteilles vidées pour faire l'appoint ; distinctes de la casse réelle. */
    nb_bouteilles_appoint: z
      .number({ invalid_type_error: "Nombre de bouteilles d'appoint invalide." })
      .int("Le nombre de bouteilles d'appoint doit être entier.")
      .min(0, "Le nombre de bouteilles d'appoint ne peut être négatif.")
      .max(NB_BOUTEILLES_MAX)
      .default(0),
    /** Volume de vin réellement réincorporé depuis ces bouteilles. */
    volume_vin_appoint_l: z
      .number({ invalid_type_error: "Volume de vin d'appoint invalide." })
      .min(0, "Le volume de vin d'appoint ne peut être négatif.")
      .max(1_000_000)
      .default(0),
    /** Origines physiques mesurées ; remplace les champs d'appoint historiques. */
    sources_appoint: sourcesAppointDegorgementSchema.optional(),
    /** Vin restant dans les bouteilles principales, avant appoint et liqueur. NULL = inconnu. */
    teneur_sucre_vin_base_g_l: dosageGLSchema.nullable().optional(),
    /** Méthode : à la glace (mécanisée) ou à la volée (manuelle). */
    methode: methodeDegorgementSchema.optional(),
    /** Dosage FINAL réel en g/L (sucres résiduels + apport liqueur). */
    dosage_final_g_l: dosageGLSchema,
    /**
     * Une liqueur d'expédition a-t-elle été ajoutée ? `false` requis pour
     * revendiquer Brut nature (défaut `true` = cas nominal dosé).
     */
    est_dose: z.boolean().default(true),
    /** Volume réellement ajouté de liqueur d'expédition, en litres. */
    volume_liqueur_expedition_l: z
      .number({ invalid_type_error: "Volume de liqueur invalide." })
      .min(0, "Le volume de liqueur ne peut être négatif.")
      .max(1_000_000)
      .default(0),
    /** Teneur mesurée avant ajout de liqueur, en g/L. */
    teneur_sucre_avant_dosage_g_l: dosageGLSchema.default(0),
    /** Augmentation du TAV due à la liqueur, en point de % vol. */
    augmentation_tav_pct_vol: z
      .number({ invalid_type_error: "Augmentation du TAV invalide." })
      .min(0, "L'augmentation du TAV ne peut être négative.")
      .max(100)
      .default(0),
    /** Mention CHOISIE parmi les compatibles (contrôle superRefine ci-dessous). */
    categorie_dosage: categorieDosageSchema,
    /** Emplacement de cave du nouveau lot dégorgé (optionnel). */
    emplacement_id: idSchema.optional(),
    /** Emplacement source vu lors de la saisie (contrôle anti-déplacement concurrent). */
    emplacement_source_id: idSchema.nullable().optional(),
    /** Lot principal ou autre lot compatible dont les bouteilles sont vidées pour l'appoint. */
    lot_appoint_source_id: idSchema.optional(),
    /** Vin prélevé directement dans une position physique du même assemblage (limite V0). */
    appoint_cuve: z
      .object({
        position_id: idSchema,
        version_position_attendue: z.string().regex(/^\d+$/),
      })
      .nullable()
      .optional(),
    /** Emplacement vu sur le lot donneur d'appoint. */
    emplacement_appoint_source_id: idSchema.nullable().optional(),
    /** Volume BVD réellement recueilli, jamais déduit du nominal. */
    volume_bvd_reel_l: z.number().min(0).max(1_000_000).optional(),
    /** Contenant canonique recevant les BVD après activation du moteur liquide. */
    contenant_bvd_id: idSchema.optional(),
    /** Bloc facultatif immuable, distinct des BVD et des volumes réincorporés. */
    reliquat_constate: reliquatConstateSchema.nullable().optional(),
    version_contenant_bvd_attendue: z.string().regex(/^\d+$/).optional(),
    /** Contexte idempotent conservé dans la file hors-ligne. */
    uuid_commande: z.string().uuid().optional(),
    version_bascule: z.string().min(1).max(80).optional(),
    /** Absents = fournis par le prestataire, donc aucun faux débit du stock du domaine. */
    bouchons: matiereDegorgementSchema.optional(),
    muselets: matiereDegorgementSchema.optional(),
    /** Opérateur de la session. */
    operateur_id: idSchema.optional(),
    /** Notes libres (liqueur utilisée, température…). */
    notes: notesSchema,
  })
  .superRefine((valeur, ctx) => {
    if (valeur.sources_appoint) {
      if (
        valeur.nb_bouteilles_appoint !== 0 ||
        valeur.volume_vin_appoint_l !== 0 ||
        valeur.appoint_cuve ||
        valeur.lot_appoint_source_id ||
        valeur.emplacement_appoint_source_id !== undefined ||
        valeur.reliquat_constate
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sources_appoint"],
          message:
            "Les sources détaillées remplacent les anciens champs d'appoint et de reliquat constaté.",
        });
      }
      if (
        !valeur.uuid_commande ||
        !valeur.version_bascule ||
        valeur.teneur_sucre_vin_base_g_l === undefined ||
        valeur.volume_bvd_reel_l === undefined
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sources_appoint"],
          message:
            "Les sources détaillées exigent le contexte hors-ligne, le volume BVD mesuré et le sucre du vin de base (ou inconnu).",
        });
      }
      const bvdPositif = (valeur.volume_bvd_reel_l ?? 0) > 0;
      if (
        bvdPositif !== Boolean(valeur.contenant_bvd_id) ||
        bvdPositif !== Boolean(valeur.version_contenant_bvd_attendue)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["contenant_bvd_id"],
          message:
            "Un volume BVD positif exige son contenant et sa version ; un zéro explicite n'en exige pas.",
        });
      }
    }
    // La mention choisie DOIT être compatible avec le dosage réel (bornes UE).
    const compatibles = mentionsCompatibles(valeur.dosage_final_g_l, valeur.est_dose);
    if (!compatibles.includes(valeur.categorie_dosage)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["categorie_dosage"],
        message:
          compatibles.length > 0
            ? `Mention incompatible avec ${valeur.dosage_final_g_l} g/L — compatibles : ${compatibles.join(", ")}.`
            : "Aucune mention compatible avec ce dosage.",
      });
    }

    if (!valeur.sources_appoint && valeur.teneur_sucre_avant_dosage_g_l > valeur.dosage_final_g_l) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["teneur_sucre_avant_dosage_g_l"],
        message: "La teneur avant dosage ne peut pas dépasser la teneur finale.",
      });
    }

    if (valeur.est_dose && valeur.volume_liqueur_expedition_l <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["volume_liqueur_expedition_l"],
        message: "Indiquez le volume réel de liqueur d'expédition ajouté.",
      });
    }
    if (!valeur.est_dose && valeur.volume_liqueur_expedition_l !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["volume_liqueur_expedition_l"],
        message: "Un vin non dosé ne peut pas avoir de liqueur d'expédition.",
      });
    }

    if (
      (!valeur.appoint_cuve &&
        valeur.nb_bouteilles_appoint === 0 &&
        valeur.volume_vin_appoint_l !== 0) ||
      (valeur.nb_bouteilles_appoint > 0 && valeur.volume_vin_appoint_l <= 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["volume_vin_appoint_l"],
        message: "Le volume d'appoint doit correspondre aux bouteilles vidées pour l'appoint.",
      });
    }
    if (
      valeur.appoint_cuve &&
      (valeur.nb_bouteilles_appoint !== 0 ||
        valeur.lot_appoint_source_id ||
        Boolean(valeur.emplacement_appoint_source_id) ||
        valeur.volume_vin_appoint_l <= 0 ||
        !valeur.version_bascule ||
        !valeur.uuid_commande)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["appoint_cuve"],
        message:
          "L'appoint en cuve exige un volume positif et un suivi matière à jour, sans bouteilles ni lot donneur d'appoint.",
      });
    }
    for (const [champ, matiere] of [
      ["bouchons", valeur.bouchons],
      ["muselets", valeur.muselets],
    ] as const) {
      if (matiere && matiere.nb_poses + matiere.nb_abimes === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [champ],
          message: "Indiquez au moins une fourniture posée ou abîmée.",
        });
      }
    }
    if (valeur.nb_bouteilles_appoint === 0 && valeur.lot_appoint_source_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["lot_appoint_source_id"],
        message: "Un lot d'appoint exige au moins une bouteille utilisée.",
      });
    }
    const contexteCanonique = [
      valeur.contenant_bvd_id,
      valeur.version_contenant_bvd_attendue,
      valeur.version_bascule,
    ];
    if (
      !valeur.sources_appoint &&
      contexteCanonique.some((champ) => champ !== undefined) &&
      contexteCanonique.some((champ) => champ === undefined)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["contenant_bvd_id"],
        message: "Le contexte BVD hors-ligne est incomplet ; rechargez les stocks liquides.",
      });
    }
  });
export type DegorgementCreerInput = z.infer<typeof degorgementCreerInput>;

// ────────────────────────────────────────────────────────────────────────────
// Input — lecture
// ────────────────────────────────────────────────────────────────────────────

/** Input `degorgement.getById` — fiche détail d'un dégorgement. */
export const degorgementGetByIdInput = z.object({ id: idSchema });
export type DegorgementGetByIdInput = z.infer<typeof degorgementGetByIdInput>;

/** Input `degorgement.lister` — filtres optionnels pour la liste tenant. */
export const degorgementListerInput = z
  .object({
    lot_source_id: idSchema.optional(),
    methode: methodeDegorgementSchema.optional(),
    categorie_dosage: categorieDosageSchema.optional(),
  })
  .optional();
export type DegorgementListerInput = z.infer<typeof degorgementListerInput>;

// ────────────────────────────────────────────────────────────────────────────
// Input — annulation (permission `stock.annulation`)
// ────────────────────────────────────────────────────────────────────────────

/** Motif libre court (tracé dans le registre et l'audit log). */
const motifSchema = z
  .string()
  .trim()
  .max(120, "Motif trop long (120 caractères max).")
  .optional()
  .or(z.literal("").transform(() => undefined));

/**
 * Input `degorgement.annuler` — annulation par OPÉRATION INVERSE complète :
 * la paire `degorgement` (± nb) + la `casse` de session sont annulées ensemble
 * (mouvements `annulation`) et le lot dégorgé passe `statut='annule'`.
 * `stock.annulerMouvement` refuse ces mouvements un à un (moitié de paire) —
 * l'annulation d'un dégorgement passe OBLIGATOIREMENT par cet endpoint.
 */
export const degorgementAnnulerInput = z.object({
  /** Dégorgement à annuler (refusé si le lot dégorgé a des mouvements aval). */
  id: idSchema,
  /** Date des mouvements d'annulation (défaut : aujourd'hui côté serveur). */
  date_mouvement: dateIsoSchema.optional(),
  /** Motif libre de l'annulation (tracé). */
  motif: motifSchema,
});
export type DegorgementAnnulerInput = z.infer<typeof degorgementAnnulerInput>;

// ────────────────────────────────────────────────────────────────────────────
// Schémas de SORTIE (validation Zod en sortie de tRPC — defense in depth)
// ────────────────────────────────────────────────────────────────────────────

/** Représentation d'un dégorgement renvoyée par l'API (NUMERIC → string). */
export const degorgementSortieSchema = z.object({
  /** Détail historique des prélèvements, fourni sur la fiche seulement. */
  sources_appoint_details: z.array(sourceAppointDetailSchema).optional(),
  composition_resultat: compositionResultatDegorgementSchema.nullable().optional(),
  decision_qualification: decisionQualificationDegorgementSchema.nullable().optional(),
  historique_qualification: z.array(decisionQualificationDegorgementSchema).optional(),
  id: idSchema,
  lot_source_id: idSchema,
  lot_appoint_source_id: idSchema.nullable(),
  position_appoint_id: idSchema.nullable().optional(),
  appoint_cuve_libelle: z.string().nullable().optional(),
  emplacement_source_id: idSchema.nullable(),
  emplacement_appoint_source_id: idSchema.nullable(),
  emplacement_destination_id: idSchema.nullable(),
  date_degorgement: z.string(),
  nb_bouteilles: z.number().int(),
  nb_pertes: z.number().int(),
  nb_bouteilles_appoint: z.number().int().nullable(),
  volume_vin_appoint_l: z.string().nullable(),
  volume_bvd_reel_l: z.string().nullable(),
  contenant_bvd_id: idSchema.nullable(),
  reliquat_constate: reliquatConstateSchema
    .extend({
      volume_mesure_l: z.string(),
      contenant_libelle: z.string(),
    })
    .nullable()
    .optional(),
  liqueur_expedition_ajoutee: z.boolean().nullable(),
  volume_liqueur_expedition_l: z.string().nullable(),
  teneur_sucre_avant_dosage_g_l: z.string().nullable(),
  augmentation_sucre_g_l: z.string().nullable(),
  augmentation_tav_pct_vol: z.string().nullable(),
  augmentation_volume_pct: z.string().nullable(),
  plafond_augmentation_volume_pct: z.string().nullable(),
  bilan_dosage_statut: z.enum(["qualifie", "a_qualifier"]),
  methode: methodeDegorgementSchema.nullable(),
  dosage_final_g_l: z.string(),
  categorie_dosage: categorieDosageSchema,
  operateur_id: idSchema.nullable(),
  notes: z.string().nullable(),
  cree_le: z.date(),
  /** Lot dégorgé créé par l'opération (lots_bouteilles.degorgement_id). */
  lot_degorge_id: idSchema.nullable(),
  reference_interne_lot_degorge: z.string().nullable(),
});
export type DegorgementSortie = z.infer<typeof degorgementSortieSchema>;

/** Sortie `degorgement.lister` — liste de dégorgements. */
export const degorgementListeSortieSchema = z.array(degorgementSortieSchema);

/** Sortie `degorgement.creer` — opération + lot créé + solde restant du source. */
export const degorgementCreerSortieSchema = z.object({
  id: idSchema,
  /** Nouveau lot au stade `degorgee` portant le dosage de la session. */
  lot_degorge_id: idSchema,
  reference_interne_lot_degorge: z.string(),
  /** Solde restant du lot source après scission (et pertes). */
  nb_bouteilles_restantes_source: z.number().int().nonnegative(),
  cree_le: z.date(),
  /** Alerte non bloquante si un lot fournisseur passe en stock négatif. */
  alerte_matieres_seches: z.string().nullable(),
});
export type DegorgementCreerSortie = z.infer<typeof degorgementCreerSortieSchema>;

/** Sortie `degorgement.annuler` — références de l'opération inverse. */
export const degorgementAnnulerSortieSchema = z.object({
  degorgement_id: idSchema,
  /** Lot lattes/pointes source, re-crédité des bouteilles et des pertes. */
  lot_source_id: idSchema,
  /** Lot dégorgé créé par l'opération, passé `statut='annule'`. */
  lot_degorge_id: idSchema,
  /** Solde du lot source après restitution (nb + pertes). */
  nb_bouteilles_source: z.number().int().nonnegative(),
  /** Mouvements `annulation` créés (paire ± nb, et casse si pertes). */
  mouvements_annulation_ids: z.array(idSchema),
});
export type DegorgementAnnulerSortie = z.infer<typeof degorgementAnnulerSortieSchema>;
